import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Users, BookOpen, Check, Shield } from "lucide-react";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RoleSelection() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  // COPPA: Only Parent and Teacher can self-register
  // Students are created by parents
  const roles = [
    {
      id: "parent",
      icon: Users,
      title: "Parent / Guardian",
      description: "Register to create accounts for your children and monitor their learning progress.",
      color: "bg-green-100 text-green-700 border-green-200",
      selectedColor: "bg-green-600 text-white border-green-600"
    },
    {
      id: "teacher",
      icon: BookOpen,
      title: "Teacher / Administrator",
      description: "Manage answer keys and view anonymized student results to track class performance.",
      color: "bg-purple-100 text-purple-700 border-purple-200",
      selectedColor: "bg-purple-600 text-white border-purple-600"
    }
  ];

  const handleContinue = async () => {
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/set-role`, { role: selectedRole });
      updateUser(response.data);
      toast.success(`Welcome! Let's get started.`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e, roleId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedRole(roleId);
    }
  };

  return (
    <div className="min-h-screen graph-paper-bg flex items-center justify-center p-6">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      
      <div className="max-w-2xl w-full" id="main-content" role="main" tabIndex={-1}>
        <div className="text-center mb-12 animate-fade-in">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6" aria-hidden="true">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 
            className="text-4xl font-bold text-gray-900 mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Welcome to MathTrack!
          </h1>
          <p className="text-lg text-gray-700 mb-2">
            Select your role to get started
          </p>
          <p className="text-sm text-gray-600 bg-green-50 border border-green-200 rounded-lg px-4 py-2 inline-block">
            <Shield className="w-4 h-4 inline mr-1 text-green-600" aria-hidden="true" />
            COPPA Compliant: Students are registered by parents
          </p>
        </div>

        <div 
          className="grid md:grid-cols-2 gap-6 mb-8" 
          role="radiogroup" 
          aria-label="Select your role"
        >
          {roles.map((role, index) => {
            const isSelected = selectedRole === role.id;
            return (
              <button
                key={role.id}
                data-testid={`role-${role.id}-btn`}
                onClick={() => setSelectedRole(role.id)}
                onKeyDown={(e) => handleKeyDown(e, role.id)}
                role="radio"
                aria-checked={isSelected}
                aria-label={`${role.title}: ${role.description}`}
                tabIndex={0}
                className={`
                  relative p-6 rounded-xl border-2 text-left transition-all
                  animate-fade-in stagger-${index + 1}
                  focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                  ${isSelected 
                    ? `${role.selectedColor} shadow-lg scale-105` 
                    : `bg-white border-gray-200 hover:border-gray-300 hover:shadow-md`
                  }
                `}
              >
                {isSelected && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-white rounded-full flex items-center justify-center" aria-hidden="true">
                    <Check className="w-4 h-4 text-gray-900" />
                  </div>
                )}
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center mb-4
                  ${isSelected ? 'bg-white/20' : role.color}
                `} aria-hidden="true">
                  <role.icon className={`w-6 h-6 ${isSelected ? 'text-white' : ''}`} />
                </div>
                <h2 
                  className={`text-xl font-semibold mb-2 ${isSelected ? 'text-white' : 'text-gray-900'}`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {role.title}
                </h2>
                <p className={`text-sm ${isSelected ? 'text-white/90' : 'text-gray-700'}`}>
                  {role.description}
                </p>
              </button>
            );
          })}
        </div>

        <div className="text-center animate-fade-in stagger-3">
          <Button
            data-testid="continue-btn"
            onClick={handleContinue}
            disabled={!selectedRole || loading}
            className="bg-primary hover:bg-primary/90 text-white px-12 py-6 text-lg rounded-md shadow-lg shadow-primary/25 disabled:opacity-50"
            aria-label={loading ? "Setting up your account" : "Continue to dashboard"}
            aria-busy={loading}
            tabIndex={0}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" role="progressbar" aria-label="Loading" />
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
